import { TabContainer, TabSection } from '@digitools/honest-react';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TEALIUM_EVENT } from '../../../../../constants/tealiumConstants';
import useAuthentication from '../../../../../hooks/useAuthentication';
import useGet from '../../../../../hooks/useGet';
import useTealium from '../../../../../hooks/useTealium';
import NotesTable from '../NotesTab/NotesTab';
import Correspondence from '../ClaimDetails/Correspondence';
import PaymentInformation from '../ClaimDetails/Payment/PaymentInformation';
import RtwDetails from '../ClaimDetails/RtwDetails';
import { RtwContext } from '../../../context';
import { decode } from '../../../utilities/hash';
import ClaimDatesCard from '../ClaimDetails/ClaimDatesCard';
import { Experience } from '../../../../../types/Experience';
import useTranslate from 'src/hooks/useTranslate';

// Helper function to determine if we can show correspondence
const showCorrespondance = (exp, userMeta) => {
  const applicationPermissions = userMeta.applicationPermissions;
  if (exp === Experience.EMPLOYER) {
    return applicationPermissions.indexOf('statusER.claimLeaveStatus.viewCorrespondence') > -1;
  } else {
    return applicationPermissions.indexOf('statusEE.claimLeaveStatus.viewCorrespondence') > -1;
  }
};

// Helper function to determine if we can show paymentInformation
const showPaymentInfo = (exp, userMeta) => {
  const applicationPermissions = userMeta.applicationPermissions;
  if (exp === Experience.EMPLOYER) {
    return applicationPermissions.indexOf('statusER.claimLeaveStatus.viewPaymentInformation') > -1;
  } else {
    return applicationPermissions.indexOf('statusEE.claimLeaveStatus.viewPaymentInformation') > -1;
  }
};

const displayRtw = (exp, userMeta) => {
  const applicationPermissions = userMeta.applicationPermissions;
  if (exp === Experience.EMPLOYER) {
    return applicationPermissions.includes('statusER.claimLeaveStatus.displayRtw');
  } else {
    return applicationPermissions.includes('statusEE.claimLeaveStatus.displayRtw');
  }
};

const hasViewClaimNotesPermission = (exp, userMeta) =>
  (exp === Experience.EMPLOYEE) || (exp === Experience.EMPLOYER && userMeta.applicationPermissions.includes('statusER.claimLeaveStatus.viewClaimNotes'));
const isValidClaimForNotesDisplay = (experience, claim) => {
  return experience === Experience.EMPLOYER ?
    (claim && claim.fundMechnCde === 'ASO' && claim.diagWaivI === 'Y') :
      true;
}

const ClaimTabs = ({ claimNum, claim, experience, userMeta, empNum, basicInfo, employeeData }) => {
  const { t } = useTranslation();
  const { t: newTranslate } = useTranslate();
  const { user } = useAuthentication();
  const { rtwNavigation } = useContext(RtwContext);
  const { trackEvent } = useTealium();
  const showPaymentTab = showPaymentInfo(experience, userMeta);
  const showCorrespondanceTab = showCorrespondance(experience, userMeta);
  const showNotesTab = hasViewClaimNotesPermission(experience, userMeta);
  const {
      data: notesData,
      error: notesError,
      loading: notesLoading,
      get: getNotes
    } = useGet({
      url: experience === Experience.EMPLOYEE ? '/status-service/notes/s1Claim' : '/status-service/notes/claim',
      noInitial: true,
      options: {
        params: { claimNum: decode(claimNum) },
      },
    });


  useEffect(()=>{
    if(showNotesTab && isValidClaimForNotesDisplay(experience, claim)){
      getNotes();
    }
  },[experience,userMeta,claim]);

  let employeeInfo = { firstName: employeeData.empFirstName, lastName: employeeData.empLastName };


  const showRTW = (user, experience, claim) => {
    if (user.customerInContext?.smallMid && displayRtw(experience, user)) {
      return claim.clmSttusCde === 'In Progress' || claim.clmSttusCde === 'Open' || claim.clmSttusCde === 'Pended';
    } else if (!user.customerInContext?.smallMid && displayRtw(experience, user)) {
      return (
        claim.clmSttusCde === 'PE' ||
        claim.clmSttusCde === 'IP' ||
        claim.clmSttusCde === 'AP' ||
        claim.clmSttusCde === 'Pending' ||
        claim.clmSttusCde === 'Pended'
      );
    }
    return false;
  };

  //Initialize tabs for arrangement for rendering
  let DatesTab, RtwTab, PaymentTab, CorrespondenceTab, NotesTab = undefined;
  //Claim Dates
  DatesTab = <TabSection
      name={t('claimDatesText')}
      label={t('claimDatesText')}
      key={'Claim Dates'}
      onClick={() =>
        trackEvent({
          event_action: TEALIUM_EVENT.EVENT_ACTION.TAB,
          event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
          event_name: 'claim dates',
          event_version: 'claim details'
        })
      }>
        <ClaimDatesCard claim={claim}/>
    </TabSection>;

  //RTW details
  const showRTWTab = showRTW(user, experience, claim);
  if (showRTWTab) {
    RtwTab = <TabSection 
        name={t('tab')} 
        label={t('tab')} 
        key={'ReturnToWork'}
        onClick={() =>
          trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.TAB,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: 'return-to-work',
            event_version: 'claim details'
          })
        }>
        <RtwDetails RtwProps={claim} product='claim' employeeInfo={employeeInfo} />
      </TabSection>;
  }
  // Payments
  if (showPaymentTab) {
    PaymentTab = <TabSection
        name={t('paymentInformationTabHeaderText')}
        label={t('paymentInformationTabHeaderText')}
        key={'Payment Information'}
        onClick={() =>
          trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.TAB,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: 'payment information',
            event_version: 'claim details'
          })
        }>
        <PaymentInformation claimNum={decode(claimNum)} empNum={empNum} claim={claim} />
      </TabSection>;
  }
  // Correspondence
  if (showCorrespondanceTab) {
    CorrespondenceTab = <TabSection
        name={t('correspondenceText')}
        label={t('correspondenceText')}
        key={'Correspondence'}
        onClick={() =>
          trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.TAB,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: 'Correspondence',
            event_version: 'claim details'
          })
        }>
        <Correspondence claimNum={decode(claimNum)} claim={claim} />
      </TabSection>
  }

  // Notes
  // TODO: FIXME: Update this var to include EE for S1 - and update the const to a let - if no data and EE we won't show
  if (showNotesTab) {
    const claimNoteBasicDetails = {
      clmLveNumber: decode(claimNum),
      determinationDate: claim.clmDtrmDte,
      status: basicInfo.status,
      employeeName: basicInfo.employeeFullName,
      location: basicInfo.location,
      subsidiary: basicInfo.subsidiary,
      userName: userMeta.firstName,
      reportType: 'Claim',
    };
    const notesTabName = experience === Experience.EMPLOYEE ? newTranslate({english: 'Status update', spanish: 'Actualización de estado'}) : 'Claim Notes';
    NotesTab = <TabSection
      // TODO: swap this back when testing with an EE
      name={notesTabName}
      key={notesTabName}
      onClick={() =>
          trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.TAB,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: notesTabName,
            event_version: 'claim details'
          })
        }>
        <NotesTable
          noteBasicDetails={claimNoteBasicDetails}
          notes={notesData}
          error={notesError}
          loading={notesLoading}
        />
    </TabSection>;
  }
  
  // Tabs won't refresh unless notesLoading is used to wrap the component
  return (
    <>
      {!notesLoading && <TabContainer data-testid='tabContainer' initialActiveTab={rtwNavigation ? t('tab') : ''}>
        {DatesTab}
        {(experience === Experience.EMPLOYEE && !notesLoading && notesData?.length > 0 ? NotesTab : undefined)}
        {RtwTab}
        {PaymentTab}
        {CorrespondenceTab}
        {(experience === Experience.EMPLOYER ? NotesTab : undefined)}
      </TabContainer>
      }
    </>
  );
};

export default ClaimTabs;
